import {Button, DropdownList, FlexColumn, FlexRow, Heading, SplitButton} from '@forecast-it/design-system';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Styled from 'styled-components/macro';
import {LoadMore} from '../../components/loaders/LoadMore';
import FinancialCalculationTrigger, {
	financialCalculationTriggerQuery,
} from '../../components/new-ui/project/project-budget-v3/loaders/FinancialCalculationTrigger';
import {createProjectUpdateToast} from '../../components/new-ui/project/project-budget-v3/util/BudgetUtils';
import {EVENT_ID, subscribe, unsubscribe} from '../../containers/event_manager';
import DirectApi, {projectUrl} from '../../directApi';
import ChangeViewButton from '../../forecast-app/shared/components/changeview/ChangeViewButton';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import CustomScrollDiv from '../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import {useEyeOptions} from '../../forecast-app/shared/hooks/useEyeOptions';
import {ContextMenuOption, EyeOptionMap} from './BaselineTypes';
import DeliverablesPhasesExportButton, {PDFProps} from './DeliverablesPhasesExportButton';
import {PhaseCard} from './PhaseDeliverablesCard';
import {
	createNodeIdLookup,
	formatAndExportCSV,
	getInitialBaselineItems,
	getInitialPhaseDeliverables,
	PHASE_COLUMNS,
	PHASE_EYE_OPTIONS,
} from './Util';
import {ContentContainer} from '../../components/initial-plan/InitialPlan.styled';
import HeaderSection from '../../components/initial-plan/HeaderSection';
import {hasPermission} from '@/forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '@/Permissions';
import type {BaselineReadonlyCompany, BaselineReadonlyProject, PhaseEdge} from './BaselineTypes';
import {Allocation, ConvertBaselineToPlaceholdersModalInput, Placeholder} from './ConvertBaselineToPlaceholdersModal';
import Util from '../../forecast-app/shared/util/util';
import {createDateFromYMD, dateObjectToIsoDate} from '../../forecast-app/shared/util/DateUtil';
import {hasModule} from '@/forecast-app/shared/util/ModuleUtil';
import {MODULE_TYPES} from '@/constants';
import {
	getDistributionArray,
	handleAllocationTotalChange,
	sumWeeklyMinutes,
} from '@/containers/modal/placeholder/PlaceholderAllocationUtils';
import ProjectUtil from '@/forecast-app/shared/util/ProjectUtil';
import {hasFeatureFlag} from '@/forecast-app/shared/util/FeatureUtil';

import {createToast} from '@/forecast-app/shared/components/toasts/another-toast/toaster';
import {activateHubspot} from '@/forecast-app/shared/util/HubspotUtil';

const DeliverablesPhasesSectionWrapper = Styled.div`
	padding: 0 32px;
`;

const StyledHeaderRow = Styled.div`
	padding: 8px 0;
`;

interface DeliverablesPhasesSectionProps {
	project: BaselineReadonlyProject;
	company: BaselineReadonlyCompany;
	pdfProps: PDFProps;
	pageRetry: () => void;
	currencySymbol: string;
	useDeliverables: boolean;
	setShowPlaceholderAlert: (show: boolean) => void;
	setPlaceholderCount: (count: number) => void;
	setPlaceholderAllocationCount: (count: number) => void;
}

export const DeliverablesPhasesSection: React.FC<DeliverablesPhasesSectionProps> = ({
	project,
	company,
	pdfProps,
	pageRetry,
	currencySymbol,
	useDeliverables,
	setShowPlaceholderAlert,
	setPlaceholderCount,
	setPlaceholderAllocationCount,
}) => {
	const intl = useIntl();
	const history = useHistory();
	const currency = (project?.rateCard ? project?.rateCard.currency : company?.currency) || '';

	const navigateToBaselineEdit = () => {
		history.push(`${projectUrl(project?.companyProjectId, project?.customProjectId)}/baseline/edit`);
	};

	const [retryCallback, setRetryCallback] = useState<() => void>();
	const [retryProcessing, setRetryProcessing] = useState(false);
	const [retryProps, setRetryProps] = useState<{noToast?: boolean}>();
	const [financialCalculationTriggerTimestamp, setFinancialCalculationTriggerTimestamp] = useState();

	const updateProject = props => {
		if (retryCallback) {
			retryCallback();
			setRetryProps(props);
			setRetryProcessing(true);
		}
	};

	const handleRetryCompletion = result => {
		if (!result.isSmallProject && !retryProps?.noToast) {
			createProjectUpdateToast(intl, result.duration);
		}
	};

	const handleFinancialCalculationTriggerResponse = (retry, result) => {
		if (!retryCallback) {
			setRetryCallback(() => retry);
		}
		if (retryProcessing && result.timestamp !== financialCalculationTriggerTimestamp) {
			handleRetryCompletion(result);
			setRetryProcessing(false);
		}
	};

	useEffect(() => {
		if (sessionStorage.getItem('baselineProjectUpdate') === 'true') {
			sessionStorage.removeItem('baselineProjectUpdate');
			setRetryProcessing(true);
		}
		subscribe(EVENT_ID.BUDGET_UPDATE_PROJECT, updateProject);
		return () => {
			unsubscribe(EVENT_ID.BUDGET_UPDATE_PROJECT, updateProject);
		};
	});

	const calculateBaselineData = (): ConvertBaselineToPlaceholdersModalInput => {
		const projectPhases = project.phases?.edges?.map(phase => {
			return phase?.node;
		});
		const uniqueBaselineRoleIds = [
			...new Set(project.phaseBaselineRoles?.edges?.map(edge => edge?.node?.role?.id).filter(Boolean)),
		];

		const placeholders: Placeholder[] = [];
		uniqueBaselineRoleIds.forEach(roleId => {
			const baselineRoles = project?.phaseBaselineRoles?.edges
				?.filter(edge => edge?.node?.role?.id === roleId)
				.map(edge => edge?.node)
				.filter(Boolean);
			if (!baselineRoles?.length) {
				return;
			}
			const roleAllocations = baselineRoles
				.map(role => {
					const phase = projectPhases?.find(phase => phase?.id === role?.globalPhaseId);
					if (
						!phase ||
						!phase.startYear ||
						!phase.startMonth ||
						!phase.startDay ||
						!phase.deadlineYear ||
						!phase.deadlineMonth ||
						!phase.deadlineDay
					) {
						return null;
					}
					const distributionArray = getDistributionArray(company);
					const result = handleAllocationTotalChange(
						role?.baselineMinutes || 0,
						distributionArray,
						sumWeeklyMinutes(company),
						Util.CreateNonUtcMomentDate(phase.startYear, phase.startMonth, phase.startDay),
						Util.CreateNonUtcMomentDate(phase.deadlineYear, phase.deadlineMonth, phase.deadlineDay)
					);
					const startDate = createDateFromYMD({
						year: phase.startYear,
						month: phase.startMonth,
						day: phase.startDay,
					});
					const endDate = createDateFromYMD({
						year: phase.deadlineYear,
						month: phase.deadlineMonth,
						day: phase.deadlineDay,
					});
					return {
						startDate: dateObjectToIsoDate(startDate),
						endDate: dateObjectToIsoDate(endDate),
						monday: result.newDistributionArray[0],
						tuesday: result.newDistributionArray[1],
						wednesday: result.newDistributionArray[2],
						thursday: result.newDistributionArray[3],
						friday: result.newDistributionArray[4],
						saturday: result.newDistributionArray[5],
						sunday: result.newDistributionArray[6],
						description: `Converted from Baseline for phase: ${phase.name}`,
					};
				})
				.filter(Boolean);
			const placeholder: Placeholder = {
				name: baselineRoles[0]?.role?.name || '',
				roleId: baselineRoles[0]?.role?.id || '',
				allocations: roleAllocations.filter(Boolean) as Allocation[],
			};
			placeholders.push(placeholder);
		});
		return {
			placeholders,
		};
	};

	const createPlaceholders = () => {
		// Calculate modal data
		const baselineData: ConvertBaselineToPlaceholdersModalInput = calculateBaselineData();
		showModal({
			type: MODAL_TYPE.CONVERT_BASELINE_TO_PLACEHOLDERS_MODAL,
			baselineData,
			projectId: project.id,
			setShowPlaceholderAlert,
			setPlaceholderCount,
			setPlaceholderAllocationCount,
		});
	};

	const createHubspotQuote = () => {
		const revenueByDeliverableId = new Map<string, number>();

		(project.phases?.edges ?? []).forEach(phaseEdge => {
			const phaseNode = phaseEdge?.node;
			(phaseNode?.phaseDeliverables?.edges ?? []).forEach(pdEdge => {
				const pd = pdEdge?.node;
				const deliverableId = pd?.deliverable?.id;
				if (!deliverableId) return;
				const revenue = pd?.financialNumbers?.baselineRevenue ?? 0;
				revenueByDeliverableId.set(deliverableId, (revenueByDeliverableId.get(deliverableId) ?? 0) + revenue);
			});
		});

		const deliverables = (project.deliverables?.edges ?? [])
			.map(edge => edge?.node)
			.filter(Boolean)
			.map(d => ({
				id: d?.id,
				name: d?.name ?? '',
				description: d?.description ?? '',
				revenue: revenueByDeliverableId.get(d?.id ?? '') ?? 0,
			}));

		if (deliverables.length === 0) return;

		const payload = {
			deliverables,
			projectId: atob(project.id).split(':')[1],
		};

		const headers = new Headers();
		headers.append('Content-Type', 'application/json');

		const initPost: RequestInit = {
			headers: headers,
			credentials: 'include',
			method: 'POST',
			body: JSON.stringify(payload),
		};

		fetch(DirectApi.graphqlServerEndpoint(`hubspot/create_quotes`), initPost)
			.then(async res => {
				if (res.status === 403) {
					const body = await res.json().catch(() => null);
					if (body && body.code === 'MISSING_SCOPES') {
						showModal({
							type: MODAL_TYPE.HUBSPOT_MISSING_SCOPES,
							onReconnect: () => {
								activateHubspot();
							},
						});
						return;
					}
				}
				if (!res.ok) {
					createToast({
						message: intl.formatMessage({id: 'push_to_crm.error_message'}),
						type: 'error',
						duration: 5000,
					});
					return;
				}
				createToast({
					message: intl.formatMessage({id: 'push_to_crm.success_message'}),
					type: 'success',
					duration: 5000,
				});
			})
			.catch(() => {
				createToast({
					message: intl.formatMessage({id: 'push_to_crm.error_message'}),
					type: 'error',
					duration: 5000,
				});
			});
	};

	const sortedPhases = useMemo(() => {
		const phaseEdges = project?.phases?.edges || [];
		return ProjectUtil.sortPhasesBySortOrder<PhaseEdge>(phaseEdges);
	}, [project?.phases?.edges]);
	const phaseLookup = createNodeIdLookup(sortedPhases.map(d => d?.node));
	const deliverableLookup = createNodeIdLookup(project.deliverables?.edges?.map(d => d?.node) ?? []);

	const [initialBaselineItems, initialPhaseDeliverables] = useMemo(
		() => [getInitialBaselineItems(project, useDeliverables), useDeliverables ? getInitialPhaseDeliverables(project) : []],
		[project]
	);

	const EYE_OPTIONS_LOCALSTORAGE_KEY = 'phase-management-eye-options';

	const [eyeOptionMap, setEyeOptionMap] = useState<EyeOptionMap>();

	const [, , eyeOptions, handleEyeOptionsChange] = useEyeOptions(
		PHASE_EYE_OPTIONS,
		EYE_OPTIONS_LOCALSTORAGE_KEY,
		undefined,
		setEyeOptionMap
	);

	const toggledColumnNames = useMemo(() => {
		return Object.entries(eyeOptionMap || {})
			.filter(([path, toggled]) => toggled && path.includes('.'))
			.map(([path]) => path.split('.').pop() as string)
			.reduce((cols: string[], col: string) => (cols.includes(col) ? cols : [...cols, col]), []);
	}, [eyeOptionMap]);

	const exportCSV = useCallback(() => {
		const filename = `${project.name}_deliverables_baseline`;
		const columnNames = [
			PHASE_COLUMNS.PHASE_NAME,
			PHASE_COLUMNS.PHASE_START_DATE,
			PHASE_COLUMNS.PHASE_DEADLINE_DATE,
			PHASE_COLUMNS.ITEM_TYPE,
			...(useDeliverables ? [PHASE_COLUMNS.DELIVERABLE_NAME] : []),
			PHASE_COLUMNS.ROLE,
			PHASE_COLUMNS.HOURS,
			PHASE_COLUMNS.EXPENSE_NAME,
			PHASE_COLUMNS.EXPENSE_CATEGORY,
			...toggledColumnNames,
		];
		formatAndExportCSV(columnNames, filename, initialBaselineItems, phaseLookup, deliverableLookup, intl);
	}, [eyeOptionMap, initialBaselineItems, phaseLookup, deliverableLookup, useDeliverables]);

	const openShowHideColumnsModal = () => {
		showModal({
			type: MODAL_TYPE.SHOW_HIDE_COLUMNS,
			eyeOptions,
			onEyeOptionsChange: handleEyeOptionsChange,
		});
	};

	const showManagePhasesModal = () => {
		showModal({
			type: MODAL_TYPE.PHASE_MANAGEMENT_MODAL,
			initialPhases: sortedPhases,
			project,
			baselineItems: initialBaselineItems,
			editBaselineDates: !project.synchBaselineAndScopingDates,
		});
	};

	const showManageDeliverablesModal = () => {
		showModal({
			type: MODAL_TYPE.MANAGE_DELIVERABLES_MODAL,
			deliverables: project.deliverables?.edges,
			projectId: project.id,
			baselineItems: initialBaselineItems,
			budgetType: project.budgetType,
		});
	};

	const editDeliverableDropdownOptions: ContextMenuOption[] = [
		...(useDeliverables
			? [
					{
						name: intl.formatMessage({id: 'deliverables.manage.modal.manage_deliverables'}),
						onClick: showManageDeliverablesModal,
					},
			  ]
			: []),
		{
			name: intl.formatMessage({id: 'deliverables.guide_section.phases.button'}),
			onClick: showManagePhasesModal,
		},
	];

	const totalFinancialNumbers = project.financialNumbers
		? {
				totalBaselineRevenue: project.financialNumbers.baselineRevenue,
				totalBaselineTimeAndExpenses: project.financialNumbers.baselineTimeAndExpenses,
				totalCost: project.financialNumbers.baselineCost,
				totalProfit: project.financialNumbers.baselineProfit,
				totalMargin: project.financialNumbers.baselineMargin,
		  }
		: {
				totalBaselineRevenue: 0,
				totalBaselineTimeAndExpenses: 0,
				totalCost: 0,
				totalProfit: 0,
				totalMargin: 0,
		  };

	const canConvertBaselineToPlaceholders = useMemo(() => {
		const isUsingProjectAllocation = company.isUsingProjectAllocation || company.isUsingMixedAllocation;
		const baselineHoursExist = (project.financialNumbers?.baselineMinutes || 0) > 0;

		return (
			hasModule(MODULE_TYPES.PLACEHOLDERS) &&
			isUsingProjectAllocation &&
			hasPermission(PERMISSION_TYPE.ALLOCATION_CREATE) &&
			baselineHoursExist
		);
	}, [company.isUsingProjectAllocation, company.isUsingMixedAllocation, project.financialNumbers?.baselineMinutes]);

	return (
		<LoadMore
			key="financial-calculation-trigger"
			query={financialCalculationTriggerQuery}
			variables={{
				projectId: project.id,
			}}
			loader={<FinancialCalculationTrigger />}
		>
			{(result, financialTriggerRetry) => {
				handleFinancialCalculationTriggerResponse(financialTriggerRetry, result);

				if (financialCalculationTriggerTimestamp !== result.timestamp) {
					setFinancialCalculationTriggerTimestamp(result.timestamp);
					pageRetry();
				}

				return (
					<CustomScrollDiv horizontalcontent>
						<ContentContainer>
							<DeliverablesPhasesSectionWrapper>
								<HeaderSection
									project={project}
									currencySymbol={currencySymbol}
									totalFinancialNumbers={totalFinancialNumbers}
								/>
								<StyledHeaderRow>
									<FlexRow justifyContent={'space-between'} gap={'s'}>
										<Heading size={4}>{intl.formatMessage({id: 'baseline.work_items'})}</Heading>
										<FlexRow justifyContent={'flex-end'}>
											{project.hubspotDeal != null && hasFeatureFlag('push_to_crm') && (
												<Button
													emphasis="medium"
													disabled={deliverableLookup.size === 0}
													onPress={createHubspotQuote}
												>
													<FormattedMessage id="push_to_crm.button_label" />
												</Button>
											)}
											<DeliverablesPhasesExportButton pdf={pdfProps} exportCSV={exportCSV} />
											{canConvertBaselineToPlaceholders && (
												<Button emphasis="medium" onPress={createPlaceholders}>
													<FormattedMessage id="common.create_placeholders" />
												</Button>
											)}
											<ChangeViewButton>
												<DropdownList.ContentGroup
													title={intl.formatMessage({id: 'change_view.table_visibility'})}
												>
													<DropdownList.Item onClick={openShowHideColumnsModal}>
														{intl.formatMessage({id: 'change_view.show_hide_columns'})}
													</DropdownList.Item>
												</DropdownList.ContentGroup>
											</ChangeViewButton>
											<SplitButton>
												<SplitButton.Section
													text={intl.formatMessage({id: 'baseline.edit_baseline.title'})}
													data-cy={'export-phases'}
													emphasis={'high'}
													onPress={navigateToBaselineEdit}
												/>
												<SplitButton.Section
													icon={'chevronDown'}
													emphasis={'high'}
													dropdownMenuOptions={editDeliverableDropdownOptions}
												/>
											</SplitButton>
										</FlexRow>
									</FlexRow>
								</StyledHeaderRow>
								<FlexColumn gap="s">
									{sortedPhases
										.map(phaseEdge => {
											if (phaseEdge?.node && company) {
												const phase = phaseEdge.node;
												const items = initialBaselineItems
													.filter(item => item.phaseId === phase.id)
													.map((item, index) => ({...item, index}));
												return (
													<PhaseCard
														useDeliverables={useDeliverables}
														phase={phase}
														currency={currency}
														company={company}
														phaseBaselineItems={items}
														project={project}
														projectPhaseDeliverables={initialPhaseDeliverables}
														eyeOptionMap={eyeOptionMap}
													/>
												);
											}
											return null;
										})
										.filter(Boolean)}
								</FlexColumn>
							</DeliverablesPhasesSectionWrapper>
						</ContentContainer>
					</CustomScrollDiv>
				);
			}}
		</LoadMore>
	);
};
